import React, { useState, useEffect } from 'react';
import { TrendingUp, Shield, Database, Zap, ArrowRight, CheckCircle, BarChart3, FileSearch, Home, Users, Award, Target, ChevronRight, ExternalLink, Menu, X, Gavel, Bot } from 'lucide-react';

const PropstenLandingPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeService, setActiveService] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showStateModal, setShowStateModal] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleAuctionClick = (e) => {
    e.preventDefault();
    setShowStateModal(true);
  };

  const handleStateSelection = (state) => {
    setShowStateModal(false);
    if (state === 'madhya-pradesh') {
      window.location.href = '/dashboard';
    } else if (state === 'maharashtra') {
      window.location.href = '/maha-rera';
    }
  };

  const services = [
    {
      icon: Database,
      title: "RERA Compliance & Risk",
      description: "Unified project profiles with delivery risk, compliance, and regulatory red flags across all states",
      features: ["Real-time RERA data", "Compliance monitoring", "Risk assessment", "Project tracking"]
    },
    {
      icon: TrendingUp,
      title: "Location Analytics & ROI",
      description: "Micro-market heatmaps with 3-5 year appreciation forecasts and infrastructure impact analysis",
      features: ["Market heatmaps", "ROI predictions", "Infrastructure impact", "Demographic trends"]
    },
    {
      icon: BarChart3,
      title: "Automated Valuation Model",
      description: "Instant fair market valuations with 95% accuracy, democratizing institutional-grade property assessment",
      features: ["AI-powered pricing", "Market comparables", "Fair value alerts", "Investment scoring"]
    },
    {
      icon: Shield,
      title: "Builder Trust Score",
      description: "Comprehensive builder ratings across delivery, legal, quality, and transparency - India's first CIBIL for builders",
      features: ["Builder ratings", "Track record", "Quality metrics", "Legal history"]
    }
  ];

  const stats = [
    { value: "$1T", label: "Market Size by 2030" },
    { value: "6M+", label: "Annual Transactions" },
    { value: "95%", label: "Valuation Accuracy" },
    { value: "60%", label: "Lifetime Savings in Property" }
  ];

  const solutions = [
    {
      title: "Pricing Transparency",
      problem: "10-30% overpayment risk",
      solution: "AI-powered fair market valuations",
      icon: TrendingUp
    },
    {
      title: "Regulatory Intelligence",
      problem: "Fragmented RERA data",
      solution: "Unified compliance tracking",
      icon: FileSearch
    },
    {
      title: "Legal Risk Mitigation",
      problem: "Title disputes & fraud",
      solution: "Automated document verification",
      icon: Shield
    },
    {
      title: "Builder Accountability",
      problem: "Delayed projects",
      solution: "Comprehensive trust scores",
      icon: Award
    }
  ];

  return (
    <div style={{
      fontFamily: '"Montserrat", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
      background: '#ffffff',
      minHeight: '100vh'
    }}>
      {/* Navigation */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: scrolled ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
        backdropFilter: scrolled ? 'blur(10px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(0,0,0,0.1)' : 'none',
        transition: 'all 0.3s ease',
        padding: '20px 0'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '0 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{
            fontSize: '24px',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-1px'
          }}>
            PROPSTEN
          </div>
          
          {/* Desktop Menu */}
          <div className="desktop-menu" style={{ display: 'flex', gap: '30px', alignItems: 'center' }}>
            <a href="#about" style={{ color: '#374151', textDecoration: 'none', fontWeight: '600', fontSize: '15px' }}>About</a>
            <a href="#services" style={{ color: '#374151', textDecoration: 'none', fontWeight: '600', fontSize: '15px' }}>Services</a>
            <a href="#founder" style={{ color: '#374151', textDecoration: 'none', fontWeight: '600', fontSize: '15px' }}>Leadership</a>
            <a href="#solutions" style={{ color: '#374151', textDecoration: 'none', fontWeight: '600', fontSize: '15px' }}>Solutions</a>
            <a 
              href="#"
              onClick={handleAuctionClick}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                padding: '10px 24px',
                borderRadius: '10px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)',
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap'
              }}
            >
              RERA Properties <Database size={16} />
            </a>
            <a 
              href="/auctions"
              style={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: 'white',
                padding: '10px 24px',
                borderRadius: '10px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 4px 16px rgba(245, 158, 11, 0.3)',
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap'
              }}
            >
              Auction Properties <Gavel size={16} />
            </a>
            <a 
              href="/legal-assistant"
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '10px 24px',
                borderRadius: '10px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap'
              }}
            >
              Legal AI <Bot size={16} />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="mobile-menu-button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              display: 'none',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            {mobileMenuOpen ? <X size={28} color="#374151" /> : <Menu size={28} color="#374151" />}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="mobile-menu" style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'white',
            borderBottom: '1px solid rgba(0,0,0,0.1)',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <a href="#about" style={{ color: '#374151', textDecoration: 'none', fontWeight: '600', fontSize: '16px', padding: '8px 0' }} onClick={() => setMobileMenuOpen(false)}>About</a>
            <a href="#services" style={{ color: '#374151', textDecoration: 'none', fontWeight: '600', fontSize: '16px', padding: '8px 0' }} onClick={() => setMobileMenuOpen(false)}>Services</a>
            <a href="#founder" style={{ color: '#374151', textDecoration: 'none', fontWeight: '600', fontSize: '16px', padding: '8px 0' }} onClick={() => setMobileMenuOpen(false)}>Leadership</a>
            <a href="#solutions" style={{ color: '#374151', textDecoration: 'none', fontWeight: '600', fontSize: '16px', padding: '8px 0' }} onClick={() => setMobileMenuOpen(false)}>Solutions</a>
            <a 
              href="#"
              onClick={(e) => {
                setMobileMenuOpen(false);
                handleAuctionClick(e);
              }}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                padding: '14px 24px',
                borderRadius: '10px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)',
                marginTop: '8px'
              }}
            >
              RERA Properties <Database size={18} />
            </a>
            <a 
              href="/auctions"
              onClick={() => setMobileMenuOpen(false)}
              style={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: 'white',
                padding: '14px 24px',
                borderRadius: '10px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: '0 4px 16px rgba(245, 158, 11, 0.3)',
                marginTop: '8px'
              }}
            >
              Auction Properties <Gavel size={18} />
            </a>
            <a 
              href="/legal-assistant"
              onClick={() => setMobileMenuOpen(false)}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '14px 24px',
                borderRadius: '10px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
                marginTop: '8px'
              }}
            >
              Legal AI Assistant <Bot size={18} />
            </a>
          </div>
        )}
      </nav>

      {/* State Selection Modal */}
      {showStateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '20px',
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            maxWidth: '600px',
            width: '100%',
            padding: '40px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            position: 'relative',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <button
              onClick={() => setShowStateModal(false)}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '50%',
                transition: 'background 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = '#f3f4f6'}
              onMouseOut={(e) => e.currentTarget.style.background = 'none'}
            >
              <X size={24} color="#374151" />
            </button>

            <div style={{
              textAlign: 'center',
              marginBottom: '32px'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                boxShadow: '0 8px 24px rgba(245, 158, 11, 0.3)'
              }}>
                <Gavel size={32} color="white" />
              </div>
              <h2 style={{
                fontSize: '28px',
                fontWeight: '800',
                color: '#111827',
                marginBottom: '12px',
                letterSpacing: '-0.5px'
              }}>
                Select State
              </h2>
              <p style={{
                fontSize: '16px',
                color: '#6b7280',
                lineHeight: '1.6'
              }}>
                Choose the state to view RERA properties
              </p>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              <button
                onClick={() => handleStateSelection('madhya-pradesh')}
                style={{
                  padding: '24px',
                  background: 'white',
                  border: '2px solid #e5e7eb',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = '#f59e0b';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(245, 158, 11, 0.15)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#111827',
                    marginBottom: '4px'
                  }}>
                    Madhya Pradesh
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#6b7280'
                  }}>
                    RERA registered projects with ML insights
                  </div>
                </div>
                <ChevronRight size={24} color="#9ca3af" />
              </button>

              <button
                onClick={() => handleStateSelection('maharashtra')}
                style={{
                  padding: '24px',
                  background: 'white',
                  border: '2px solid #e5e7eb',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = '#f59e0b';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(245, 158, 11, 0.15)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#111827',
                    marginBottom: '4px'
                  }}>
                    Maharashtra
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#6b7280'
                  }}>
                    RERA registered projects
                  </div>
                </div>
                <ChevronRight size={24} color="#9ca3af" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="hero-section" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        paddingTop: '80px'
      }}>
        {/* Animated Background Circles */}
        <div style={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'float 20s ease-in-out infinite'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '10%',
          left: '5%',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(5, 150, 105, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'float 25s ease-in-out infinite reverse'
        }} />

        <div className="hero-content" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '40px 20px',
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '40px',
          alignItems: 'center',
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              display: 'inline-block',
              background: 'rgba(16, 185, 129, 0.1)',
              padding: '8px 20px',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid rgba(16, 185, 129, 0.3)'
            }}>
              <span style={{ color: '#059669', fontWeight: '700', fontSize: '12px', letterSpacing: '1px' }}>
                AI-POWERED REAL ESTATE INTELLIGENCE
              </span>
            </div>
            
            <h1 className="hero-title" style={{
              fontSize: '48px',
              fontWeight: '900',
              lineHeight: '1.1',
              marginBottom: '20px',
              color: '#111827',
              letterSpacing: '-1px'
            }}>
              India's Leading <span style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>Property Intelligence</span> Platform
            </h1>
            
            <p className="hero-description" style={{
              fontSize: '18px',
              color: '#6b7280',
              lineHeight: '1.6',
              marginBottom: '32px',
              fontWeight: '500',
              maxWidth: '800px',
              margin: '0 auto 32px'
            }}>
              Making informed property decisions through AI-driven valuation, comprehensive RERA compliance tracking, and real-time market intelligence.
            </p>

            <div className="hero-buttons" style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a
                href="/dashboard"
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  padding: '16px 32px',
                  borderRadius: '12px',
                  textDecoration: 'none',
                  fontWeight: '700',
                  fontSize: '16px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                Explore Projects <ChevronRight size={20} />
              </a>
              <a
                href="#services"
                style={{
                  background: 'white',
                  color: '#10b981',
                  padding: '16px 32px',
                  borderRadius: '12px',
                  textDecoration: 'none',
                  fontWeight: '700',
                  fontSize: '16px',
                  border: '2px solid #10b981',
                  transition: 'all 0.3s ease',
                  display: 'inline-flex',
                  alignItems: 'center'
                }}
              >
                Learn More
              </a>
            </div>

            {/* Trust Indicators */}
            <div className="trust-indicators" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '20px',
              marginTop: '48px',
              maxWidth: '600px',
              margin: '48px auto 0'
            }}>
              {[
                { icon: Shield, text: "RERA Verified" },
                { icon: CheckCircle, text: "AI Validated" },
                { icon: Award, text: "Trusted by 10K+" }
              ].map((item, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px'
                }}>
                  <item.icon size={20} color="#10b981" strokeWidth={2.5} />
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                    {item.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section style={{
        padding: '60px 20px',
        background: 'white'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '24px'
        }}>
          {stats.map((stat, idx) => (
            <div key={idx} style={{
              textAlign: 'center',
              padding: '32px 20px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #f9fafb 0%, #ffffff 100%)',
              border: '1px solid #e5e7eb',
              transition: 'all 0.3s ease'
            }}>
              <div style={{
                fontSize: '40px',
                fontWeight: '900',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '12px'
              }}>
                {stat.value}
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280', fontWeight: '600' }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* About Section */}
      <section id="about" style={{
        padding: '80px 20px',
        background: 'linear-gradient(135deg, #f9fafb 0%, #ffffff 100%)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '40px',
          alignItems: 'center'
        }}>
          <div>
            <div style={{
              display: 'inline-block',
              background: 'rgba(16, 185, 129, 0.1)',
              padding: '8px 20px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <span style={{ color: '#059669', fontWeight: '700', fontSize: '12px', letterSpacing: '1px' }}>
                ABOUT PROPSTEN
              </span>
            </div>
            <h2 style={{
              fontSize: '36px',
              fontWeight: '900',
              lineHeight: '1.2',
              marginBottom: '20px',
              color: '#111827'
            }}>
              Transforming India's Real Estate Market
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#6b7280',
              lineHeight: '1.7',
              marginBottom: '24px'
            }}>
              India's residential market is poised for exponential expansion, projected to reach <strong>$1 trillion by 2030</strong>. However, 60% of household wealth invested in property is made without adequate data or structured intelligence.
            </p>
            <p style={{
              fontSize: '16px',
              color: '#6b7280',
              lineHeight: '1.7',
              marginBottom: '24px'
            }}>
              Propsten addresses this critical gap by combining <strong>RERA compliance data</strong>, <strong>AI-led valuation</strong>, and <strong>comprehensive risk analytics</strong> into a unified platform.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                "95% valuation accuracy",
                "Real-time RERA tracking",
                "Comprehensive builder scores",
                "3-5 year forecasts"
              ].map((item, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <CheckCircle size={18} color="#10b981" />
                  <span style={{ fontSize: '15px', color: '#374151', fontWeight: '500' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" style={{
        padding: '80px 20px',
        background: 'white'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <div style={{
              display: 'inline-block',
              background: 'rgba(16, 185, 129, 0.1)',
              padding: '8px 20px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <span style={{ color: '#059669', fontWeight: '700', fontSize: '12px', letterSpacing: '1px' }}>
                OUR SERVICES
              </span>
            </div>
            <h2 style={{
              fontSize: '36px',
              fontWeight: '900',
              lineHeight: '1.2',
              marginBottom: '16px',
              color: '#111827'
            }}>
              Intelligence Engines
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#6b7280',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              AI-powered intelligence layers for comprehensive property insights
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px'
          }}>
            {services.map((service, idx) => (
              <div
                key={idx}
                style={{
                  background: 'linear-gradient(135deg, #f9fafb 0%, #ffffff 100%)',
                  padding: '32px',
                  borderRadius: '20px',
                  border: '1px solid #e5e7eb',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
              >
                <service.icon 
                  size={40} 
                  color='#10b981'
                  strokeWidth={2}
                  style={{ marginBottom: '20px' }}
                />
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '800',
                  marginBottom: '12px',
                  color: '#111827'
                }}>
                  {service.title}
                </h3>
                <p style={{
                  fontSize: '14px',
                  lineHeight: '1.6',
                  marginBottom: '20px',
                  color: '#6b7280'
                }}>
                  {service.description}
                </p>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  {service.features.map((feature, fidx) => (
                    <div key={fidx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <CheckCircle size={14} color='#10b981' />
                      <span style={{
                        fontSize: '13px',
                        fontWeight: '500',
                        color: '#374151'
                      }}>
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section id="solutions" style={{
        padding: '80px 20px',
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <div style={{
              display: 'inline-block',
              background: 'rgba(16, 185, 129, 0.1)',
              padding: '8px 20px',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid rgba(16, 185, 129, 0.3)'
            }}>
              <span style={{ color: '#059669', fontWeight: '700', fontSize: '12px', letterSpacing: '1px' }}>
                SOLVING REAL PROBLEMS
              </span>
            </div>
            <h2 style={{
              fontSize: '36px',
              fontWeight: '900',
              lineHeight: '1.2',
              color: '#111827'
            }}>
              Addressing Real Estate Challenges
            </h2>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px'
          }}>
            {solutions.map((solution, idx) => (
              <div key={idx} style={{
                background: 'white',
                padding: '32px',
                borderRadius: '20px',
                border: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.08)'
              }}>
                <solution.icon size={36} color="#10b981" strokeWidth={2} style={{ marginBottom: '20px' }} />
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '800',
                  marginBottom: '16px',
                  color: '#111827'
                }}>
                  {solution.title}
                </h3>
                <div style={{
                  background: '#fef2f2',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  marginBottom: '12px',
                  border: '1px solid #fecaca'
                }}>
                  <div style={{ fontSize: '11px', color: '#991b1b', fontWeight: '600', marginBottom: '4px' }}>
                    PROBLEM
                  </div>
                  <div style={{ fontSize: '14px', color: '#dc2626', fontWeight: '700' }}>
                    {solution.problem}
                  </div>
                </div>
                <div style={{
                  background: '#f0fdf4',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '1px solid #bbf7d0'
                }}>
                  <div style={{ fontSize: '11px', color: '#166534', fontWeight: '600', marginBottom: '4px' }}>
                    SOLUTION
                  </div>
                  <div style={{ fontSize: '14px', color: '#16a34a', fontWeight: '700' }}>
                    {solution.solution}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '80px 20px',
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1
        }}>
          <h2 style={{
            fontSize: '40px',
            fontWeight: '900',
            color: 'white',
            marginBottom: '20px',
            lineHeight: '1.2'
          }}>
            Ready to Make Smarter Property Decisions?
          </h2>
          <p style={{
            fontSize: '18px',
            color: 'rgba(255,255,255,0.9)',
            marginBottom: '40px',
            lineHeight: '1.6'
          }}>
            Explore 500,000+ RERA-verified projects with AI-powered insights
          </p>
          <a
            href="/dashboard"
            style={{
              background: 'white',
              color: '#10b981',
              padding: '18px 40px',
              borderRadius: '12px',
              textDecoration: 'none',
              fontWeight: '800',
              fontSize: '16px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              boxShadow: '0 12px 40px rgba(0,0,0,0.2)'
            }}
          >
            View Properties Now <ArrowRight size={20} />
          </a>
        </div>
      </section>

      {/* Founder Section - NEW */}
      <section id="founder" style={{
        padding: '80px 20px',
        background: 'linear-gradient(135deg, #f9fafb 0%, #ffffff 100%)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <div style={{
              display: 'inline-block',
              background: 'rgba(16, 185, 129, 0.1)',
              padding: '8px 20px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <span style={{ color: '#059669', fontWeight: '700', fontSize: '12px', letterSpacing: '1px' }}>
                LEADERSHIP
              </span>
            </div>
            <h2 style={{
              fontSize: '36px',
              fontWeight: '900',
              lineHeight: '1.2',
              marginBottom: '16px',
              color: '#111827'
            }}>
              Built by IIT/IIM Professionals
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#6b7280',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Visionary leadership driving real estate intelligence innovation
            </p>
          </div>

          <div style={{
            background: 'white',
            borderRadius: '24px',
            padding: '48px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.05)'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '200px 1fr',
              gap: '40px',
              alignItems: 'start'
            }} className="founder-grid">
              {/* Founder Image/Avatar */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '20px'
              }}>
                <div style={{
                  width: '180px',
                  height: '180px',
                  borderRadius: '20px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '64px',
                  fontWeight: '900',
                  color: 'white',
                  boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)',
                  fontFamily: '"Montserrat", sans-serif',
                  letterSpacing: '-2px'
                }}>
                  P
                </div>
                <div style={{ textAlign: 'center' }}>
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: '800',
                    color: '#111827',
                    marginBottom: '4px'
                  }}>
                    Leadership Team
                  </h3>
                  <p style={{
                    fontSize: '13px',
                    color: '#667eea',
                    fontWeight: '600',
                    letterSpacing: '0.5px'
                  }}>
                    IIT/IIM Alumni
                  </p>
                  <div style={{
                    marginTop: '12px',
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'center',
                    flexWrap: 'wrap'
                  }}>
                    <span style={{
                      background: '#f3f4f6',
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      color: '#374151'
                    }}>
                      IIT Alumni
                    </span>
                    <span style={{
                      background: '#f3f4f6',
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      color: '#374151'
                    }}>
                      IIM Alumni
                    </span>
                    <span style={{
                      background: '#f3f4f6',
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      color: '#374151'
                    }}>
                      Banking Veterans
                    </span>
                  </div>
                </div>
              </div>

              {/* Founder Bio */}
              <div>
                <div style={{
                  fontSize: '15px',
                  lineHeight: '1.8',
                  color: '#374151',
                  marginBottom: '32px'
                }}>
                  <p style={{ marginBottom: '16px' }}>
                    Led by <strong>IIT and IIM alumni</strong> with extensive experience in <strong>banking, mortgage lending, legal due diligence, and asset monetization</strong>, Propsten is reshaping India's property investment landscape through AI-enabled Property Intelligence. Our team's deep industry expertise has uncovered critical information gaps plaguing real estate transactions.
                  </p>
                  <p>
                    At Propsten, we've built India's most advanced PropTech intelligence suite:
                  </p>
                </div>

                {/* Three Key Innovations */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: '20px',
                  marginBottom: '32px'
                }}>
                  {/* RERA Intelligence Engine */}
                  <div style={{
                    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '2px solid #bae6fd'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'start',
                      gap: '12px'
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        background: '#0284c7',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <Database size={20} color="white" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{
                          fontSize: '16px',
                          fontWeight: '700',
                          color: '#0c4a6e',
                          marginBottom: '8px'
                        }}>
                          RERA Intelligence Engine
                        </h4>
                        <p style={{
                          fontSize: '14px',
                          lineHeight: '1.6',
                          color: '#075985'
                        }}>
                          Propsten's flagship platform analyzes real estate projects across multiple states, computing real-time risk scores, builder credibility indices, and delivery predictions. The system processes regulatory data, financial metrics, and historical patterns to classify projects into 5 risk tiers—helping investors identify opportunities and avoid traps.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Automated Due Diligence */}
                  <div style={{
                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '2px solid #fbbf24'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'start',
                      gap: '12px'
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        background: '#d97706',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <Shield size={20} color="white" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{
                          fontSize: '16px',
                          fontWeight: '700',
                          color: '#78350f',
                          marginBottom: '8px'
                        }}>
                          Automated Due Diligence
                        </h4>
                        <p style={{
                          fontSize: '14px',
                          lineHeight: '1.6',
                          color: '#92400e'
                        }}>
                          From instant title checks to fraud analytics, Propsten's AI-powered tools reduce due diligence time from months to minutes while improving accuracy coupled with platform's unique capability to process title verifications and identified high-risk projects before investment.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Predictive Analytics */}
                  <div style={{
                    background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '2px solid #f9a8d4'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'start',
                      gap: '12px'
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        background: '#be185d',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <TrendingUp size={20} color="white" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{
                          fontSize: '16px',
                          fontWeight: '700',
                          color: '#831843',
                          marginBottom: '8px'
                        }}>
                          Predictive Analytics
                        </h4>
                        <p style={{
                          fontSize: '14px',
                          lineHeight: '1.6',
                          color: '#9f1239'
                        }}>
                          Leveraging AI/machine learning, Propsten forecasts project delays, cost escalations, and builder reliability—giving investors the foresight needed in India's complex real estate market.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mission Statement */}
                <div style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  padding: '24px',
                  borderRadius: '12px',
                  textAlign: 'center'
                }}>
                  <p style={{
                    fontSize: '16px',
                    lineHeight: '1.7',
                    color: 'white',
                    fontWeight: '500',
                    fontStyle: 'italic'
                  }}>
                    Ranjit's mission is singular: <strong>Democratize real estate intelligence and make property investment decisions as informed and transparent as financial markets.</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Terms & Conditions Modal */}
      {showTermsModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px',
            overflowY: 'auto'
          }}
          onClick={() => setShowTermsModal(false)}
        >
          <div 
            style={{
              background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
              borderRadius: '16px',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '24px 32px',
              borderBottom: '1px solid rgba(16, 185, 129, 0.2)',
              position: 'sticky',
              top: 0,
              background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
              zIndex: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{
                fontSize: '28px',
                fontWeight: '800',
                margin: 0,
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                Terms & Conditions
              </h2>
              <button
                onClick={() => setShowTermsModal(false)}
                style={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#ef4444',
                  fontSize: '20px',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
              >
                <X size={20} />
              </button>
            </div>

            {/* Content - Scrollable */}
            <div style={{
              padding: '32px',
              color: '#e5e7eb',
              lineHeight: '1.8',
              fontSize: '14px'
            }}>
              <p style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '24px' }}>
                Last Updated: January 2025
              </p>

              <p style={{ marginBottom: '24px' }}>
                Welcome to <strong style={{ color: '#10b981' }}>Propsten.com</strong>. By accessing or using this Website, you agree to comply with these Terms and Conditions.
              </p>

              <div style={{ marginBottom: '28px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#10b981', marginBottom: '12px' }}>
                  1. About Propsten
                </h3>
                <p>Propsten curates RERA projects, auction properties, distressed opportunities, and market analytics. We may facilitate acquisitions and provide financial solutions.</p>
              </div>

              <div style={{ marginBottom: '28px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#10b981', marginBottom: '12px' }}>
                  2. Sources of Information
                </h3>
                <p style={{ marginBottom: '12px' }}><strong>RERA Data:</strong> From official portals and government sources.</p>
                <p style={{ marginBottom: '12px' }}><strong>Auction Data:</strong> From newspapers, bank notices, websites, and public records.</p>
                <div style={{
                  padding: '12px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  color: '#fca5a5',
                  fontSize: '13px'
                }}>
                  ⚠ Propsten is not affiliated with any government, RERA body, bank, or newspaper unless stated.
                </div>
              </div>

              <div style={{ marginBottom: '28px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#10b981', marginBottom: '12px' }}>
                  3. Accuracy & Disclaimer
                </h3>
                <p>We don't guarantee completeness or accuracy. Users must verify independently. We're not liable for losses from Website reliance.</p>
              </div>

              <div style={{ marginBottom: '28px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#10b981', marginBottom: '12px' }}>
                  4. Intellectual Property
                </h3>
                <div style={{
                  padding: '12px',
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: '8px',
                  marginBottom: '12px'
                }}>
                  All content, layouts, datasets, analytics, and design are Propsten's exclusive property.
                </div>
                <p>No copying, scraping, reproducing, or reselling without written consent. Personal non-commercial use only.</p>
              </div>

              <div style={{ marginBottom: '28px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#10b981', marginBottom: '12px' }}>
                  5. Prohibited Activities
                </h3>
                <p>No bots, scrapers, security circumvention, server interference, data resale, or competitive use.</p>
              </div>

              <div style={{ marginBottom: '28px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#10b981', marginBottom: '12px' }}>
                  6-11. Additional Terms
                </h3>
                <p style={{ marginBottom: '8px' }}><strong>6.</strong> Content is not professional advice.</p>
                <p style={{ marginBottom: '8px' }}><strong>7.</strong> Not responsible for third-party links.</p>
                <p style={{ marginBottom: '8px' }}><strong>8.</strong> No liability for damages from Website use.</p>
                <p style={{ marginBottom: '8px' }}><strong>9.</strong> You indemnify Propsten against misuse claims.</p>
                <p style={{ marginBottom: '8px' }}><strong>10.</strong> We may terminate access for violations.</p>
                <p><strong>11.</strong> Governed by Indian law.</p>
              </div>

              {/* Contact */}
              <div style={{
                marginTop: '32px',
                padding: '20px',
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '12px'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#10b981', marginBottom: '12px' }}>
                  12. Contact
                </h3>
                <p style={{ marginBottom: '4px' }}>
                  Website: <a href="https://www.propsten.com" style={{ color: '#10b981', textDecoration: 'none' }}>www.propsten.com</a>
                </p>
                <p>
                  Email: <a href="mailto:info@propsten.com" style={{ color: '#10b981', textDecoration: 'none' }}>info@propsten.com</a>
                </p>
              </div>
            </div>

            {/* Footer Button */}
            <div style={{
              padding: '20px 32px',
              borderTop: '1px solid rgba(16, 185, 129, 0.2)',
              position: 'sticky',
              bottom: 0,
              background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => setShowTermsModal(false)}
                style={{
                  padding: '12px 32px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer style={{
        padding: '60px 20px 30px',
        background: '#111827',
        color: 'white'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '40px',
            marginBottom: '40px'
          }}>
            <div>
              <div style={{
                fontSize: '24px',
                fontWeight: '800',
                marginBottom: '16px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                PROPSTEN
              </div>
              <p style={{
                fontSize: '14px',
                color: '#9ca3af',
                lineHeight: '1.6',
                marginBottom: '20px'
              }}>
                India's leading AI-powered property intelligence platform.
              </p>
            </div>
            <div>
              <h4 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '16px' }}>Services</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <a href="#services" style={{ color: '#9ca3af', textDecoration: 'none', fontSize: '13px' }}>RERA Compliance</a>
                <a href="#services" style={{ color: '#9ca3af', textDecoration: 'none', fontSize: '13px' }}>Valuation</a>
                <a href="#services" style={{ color: '#9ca3af', textDecoration: 'none', fontSize: '13px' }}>Analytics</a>
              </div>
            </div>
            <div>
              <h4 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '16px' }}>Legal</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button 
                  onClick={() => setShowTermsModal(true)}
                  style={{ 
                    color: '#9ca3af', 
                    textDecoration: 'none', 
                    fontSize: '13px',
                    background: 'none',
                    border: 'none',
                    padding: '0',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#10b981'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#9ca3af'}
                >
                  Terms & Conditions
                </button>
              </div>
            </div>
            <div>
              <h4 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '16px' }}>Contact</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px', color: '#9ca3af' }}>
                <div>Mumbai, Maharashtra, India</div>
                <div>contactpropsten@gmail.com</div>
              </div>
            </div>
          </div>
          <div style={{
            paddingTop: '30px',
            borderTop: '1px solid #374151',
            textAlign: 'center',
            fontSize: '13px',
            color: '#9ca3af',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}>
            <div>
              © 2026 Propsten. All rights reserved.
            </div>
            <div>
              <button
                onClick={() => setShowTermsModal(true)}
                style={{
                  color: '#10b981',
                  textDecoration: 'none',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '13px',
                  transition: 'color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.color = '#059669'}
                onMouseOut={(e) => e.currentTarget.style.color = '#10b981'}
              >
                View Terms & Conditions
              </button>
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&display=swap');
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          50% {
            transform: translateY(-20px) translateX(10px);
          }
        }
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        html {
          scroll-behavior: smooth;
        }
        
        body {
          overflow-x: hidden;
        }

        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
          .founder-grid {
            grid-template-columns: 1fr !important;
            gap: 30px !important;
          }

          .desktop-menu {
            display: none !important;
          }
          
          .mobile-menu-button {
            display: block !important;
          }
          
          .hero-title {
            font-size: 32px !important;
          }
          
          .hero-description {
            font-size: 16px !important;
          }
          
          .hero-buttons {
            flex-direction: column !important;
          }
          
          .hero-buttons a {
            width: 100%;
            justify-content: center !important;
          }
          
          .trust-indicators {
            grid-template-columns: 1fr !important;
          }
          
          .hero-content {
            grid-template-columns: 1fr !important;
          }
        }

        @media (min-width: 769px) {
          .mobile-menu-button {
            display: none !important;
          }
          
          .hero-content {
            grid-template-columns: 1fr 1fr !important;
          }
          
          .founder-grid {
            grid-template-columns: 200px 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PropstenLandingPage;